import { useMemo, useState } from "react";
import { Package, Wallet } from "lucide-react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  MARKETPLACE_PAYOUT_STATUS_LABELS,
  marketplaceFulfilmentMethodLabel,
  marketplaceFulfilmentStatusLabel,
  SELLER_SETTLEMENT_BATCH_STATUS_LABELS,
  orderStatusLabel,
} from "@gaole/shared";
import { api, ApiError, apiDownload } from "../lib/api";
import { fmtDate, rm } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { Badge, Button, Input, SearchSelect, statusColor } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { flattenInfinitePages, getNextPageParam } from "../lib/infinite-list";
import { showQueryLoader } from "../lib/query-utils";

type HubTab = "orders" | "settlements";

type OrderView =
  | "ALL"
  | "PENDING_PAYMENT"
  | "PAID"
  | "PROCESSING"
  | "SHIPPED"
  | "PICKUP_READY"
  | "COMPLETED"
  | "CANCELLED"
  | "PAYOUT_READY"
  | "SETTLED";

interface OrderRow {
  id: string;
  orderNo: string;
  status: string;
  sellerFulfilmentStatus: string | null;
  payoutStatus: string | null;
  fulfilmentMethod: string;
  total: number;
  sellerNet: number | null;
  platformFee: number | null;
  trackingNo: string | null;
  courier: string | null;
  cancelReason: string | null;
  paidAt: string | null;
  createdAt: string;
  customer: string;
  shopName: string;
  items: { name: string; quantity: number }[];
}

interface SettlementRow {
  id: string;
  shopName: string;
  periodStart: string;
  periodEnd: string;
  orderCount: number;
  netTotal: number;
  status: string;
  createdAt: string;
}

function orderViews(collectLabel: string): { value: OrderView; label: string }[] {
  return [
    { value: "ALL", label: "All" },
    { value: "PENDING_PAYMENT", label: "Confirm" },
    { value: "PAID", label: "Pending" },
    { value: "PROCESSING", label: "Processing" },
    { value: "SHIPPED", label: "Shipped" },
    { value: "PICKUP_READY", label: `${collectLabel} ready` },
    { value: "COMPLETED", label: "Completed" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "PAYOUT_READY", label: "Payout ready" },
    { value: "SETTLED", label: "Settled" },
  ];
}

const SETTLEMENT_VIEWS: { value: string; label: string }[] = [
  { value: "FINALIZED", label: "Awaiting payout" },
  { value: "PAID_OUT", label: "Paid out" },
  { value: "ALL", label: "All" },
];

const PAGE_SIZE = 25;

function fulfilmentLabel(row: OrderRow, collectLabel: string): string {
  if (row.status === "PENDING_PAYMENT") return orderStatusLabel("PENDING_PAYMENT");
  if (row.status === "CANCELLED") return marketplaceFulfilmentStatusLabel("CANCELLED");
  if (row.payoutStatus === "SETTLED") return MARKETPLACE_PAYOUT_STATUS_LABELS.SETTLED;
  if (row.payoutStatus === "READY") return MARKETPLACE_PAYOUT_STATUS_LABELS.READY;
  if (row.sellerFulfilmentStatus) {
    return marketplaceFulfilmentStatusLabel(row.sellerFulfilmentStatus, collectLabel);
  }
  return orderStatusLabel(row.status);
}

function fulfilmentBadgeColor(row: OrderRow): "green" | "amber" | "pink" | "gray" | "red" {
  if (row.status === "PENDING_PAYMENT") return "amber";
  if (row.status === "CANCELLED") return "red";
  if (row.sellerFulfilmentStatus === "SHIPPED" || row.sellerFulfilmentStatus === "READY_FOR_COLLECTION") return "pink";
  if (row.sellerFulfilmentStatus === "BUYER_CONFIRMED" || row.payoutStatus === "SETTLED") return "green";
  return "gray";
}

export function MarketplaceSales() {
  const qc = useQueryClient();
  const canManage = useAuth((s) => s.can("marketplace.manage"));
  const collectLabel = useAuth((s) => s.config?.marketplaceCollectLabel ?? "COD");
  const [searchParams, setSearchParams] = useSearchParams();
  const hub = (searchParams.get("hub") as HubTab | null) ?? "orders";
  const orderView = (searchParams.get("view") as OrderView | null) ?? "ALL";
  const settlementView = searchParams.get("settlementStatus") ?? "FINALIZED";
  const search = searchParams.get("search") ?? "";
  const [searchInput, setSearchInput] = useState(search);
  const [orderSelectedId, setOrderSelectedId] = useState<string | null>(null);
  const [batchSelectedId, setBatchSelectedId] = useState<string | null>(null);
  const [selectedBatchKeys, setSelectedBatchKeys] = useState<Set<string>>(() => new Set());
  const [settleSellerId, setSettleSellerId] = useState("");
  const [settlePreview, setSettlePreview] = useState<{ readyOrderCount: number; readyNetTotal: number } | null>(null);

  const sellersQuery = useQuery({
    queryKey: ["marketplace-sellers-pick"],
    queryFn: () => api<{ items: Array<{ id: string; shopName: string; shopSlug: string }> }>("/marketplace/admin/sellers?pageSize=200"),
    enabled: hub === "settlements" && canManage,
  });

  const generateSettleMut = useMutation({
    mutationFn: () =>
      api("/marketplace/admin/settlements/generate", {
        method: "POST",
        body: { sellerId: settleSellerId, to: new Date().toISOString() },
      }),
    onSuccess: () => {
      toast("success", "Settlement batch created");
      setSettlePreview(null);
      void qc.invalidateQueries({ queryKey: ["marketplace-settlements"] });
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Generate failed"),
  });

  const ordersQuery = useInfiniteQuery({
    queryKey: ["marketplace-orders", orderView, search],
    initialPageParam: 1,
    enabled: hub === "orders",
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ page: String(pageParam), pageSize: String(PAGE_SIZE), view: orderView });
      if (search.trim()) params.set("search", search.trim());
      return api<{ items: OrderRow[]; total: number }>(`/marketplace/admin/orders?${params}`);
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(lastPage.total, allPages.reduce((acc, p) => acc + p.items.length, 0), PAGE_SIZE),
  });

  const settlementsQuery = useInfiniteQuery({
    queryKey: ["marketplace-settlements", settlementView],
    initialPageParam: 1,
    enabled: hub === "settlements",
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ page: String(pageParam), pageSize: String(PAGE_SIZE) });
      if (settlementView !== "ALL") params.set("status", settlementView);
      return api<{ items: SettlementRow[]; total: number }>(`/marketplace/admin/settlements?${params}`);
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(lastPage.total, allPages.reduce((acc, p) => acc + p.items.length, 0), PAGE_SIZE),
  });

  const batchDetailQuery = useQuery({
    queryKey: ["marketplace-settlement", batchSelectedId],
    queryFn: () =>
      api<{
        batch: SettlementRow & { grossTotal: number; feeTotal: number };
        orders: Array<{
          id: string;
          orderNo: string;
          subtotal: number;
          sellerNet: number | null;
          paidAt: string | null;
          customer: string;
          sellerFulfilmentStatus: string | null;
          payoutStatus: string | null;
        }>;
      }>(`/marketplace/admin/settlements/${batchSelectedId}`),
    enabled: !!batchSelectedId,
  });

  const markPaidMut = useMutation({
    mutationFn: (batchIds: string[]) =>
      api("/marketplace/admin/settlements/mark-paid-out", { method: "POST", body: { batchIds } }),
    onSuccess: () => {
      toast("success", "Marked as paid out");
      setSelectedBatchKeys(new Set());
      void qc.invalidateQueries({ queryKey: ["marketplace-settlements"] });
      void qc.invalidateQueries({ queryKey: ["marketplace-orders"] });
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Update failed"),
  });

  const orderRows = flattenInfinitePages(ordersQuery.data?.pages);
  const orderTotal = ordersQuery.data?.pages[0]?.total ?? 0;
  const orderHasNext = orderRows.length < orderTotal && (ordersQuery.hasNextPage ?? false);

  const settlementRows = flattenInfinitePages(settlementsQuery.data?.pages);
  const settlementTotal = settlementsQuery.data?.pages[0]?.total ?? 0;
  const settlementHasNext = settlementRows.length < settlementTotal && (settlementsQuery.hasNextPage ?? false);

  const orderColumns = useMemo<Column<OrderRow>[]>(
    () => [
      { key: "order", header: "Order", sortable: true, sortValue: (r) => r.orderNo, render: (r) => r.orderNo },
      {
        key: "items",
        header: "Items",
        hideSm: true,
        render: (r) => (
          <span className="max-w-[12rem] truncate text-xs text-white/70" title={r.items.map((i) => i.name).join(", ")}>
            {r.items.map((i) => (i.quantity > 1 ? `${i.name} ×${i.quantity}` : i.name)).join(" · ") || "—"}
          </span>
        ),
      },
      { key: "shop", header: "Shop", sortValue: (r) => r.shopName, render: (r) => r.shopName },
      { key: "customer", header: "Customer", hideSm: true, render: (r) => r.customer },
      {
        key: "fulfilment",
        header: "Fulfilment",
        exportValue: (r) => fulfilmentLabel(r, collectLabel),
        render: (r) => (
          <div className="flex flex-col items-start gap-0.5">
            <Badge color={fulfilmentBadgeColor(r)}>{fulfilmentLabel(r, collectLabel)}</Badge>
            <span className="text-[10px] text-muted">{marketplaceFulfilmentMethodLabel(r.fulfilmentMethod, collectLabel)}</span>
          </div>
        ),
      },
      {
        key: "tracking",
        header: "Tracking",
        hideSm: true,
        exportValue: (r) => (r.trackingNo ? `${r.courier ?? ""} ${r.trackingNo}`.trim() : ""),
        render: (r) =>
          r.trackingNo ? (
            <span className="text-xs text-muted">
              {r.courier ? `${r.courier} · ` : ""}
              <span className="font-mono text-foreground">{r.trackingNo}</span>
            </span>
          ) : (
            "—"
          ),
      },
      {
        key: "payout",
        header: "Payout",
        render: (r) =>
          r.payoutStatus ? (
            <Badge color={statusColor(r.payoutStatus === "SETTLED" ? "DELIVERED" : "PENDING")}>
              {MARKETPLACE_PAYOUT_STATUS_LABELS[r.payoutStatus] ?? r.payoutStatus}
            </Badge>
          ) : (
            "—"
          ),
        hideSm: true,
      },
      { key: "total", header: "Total", render: (r) => rm(r.total), className: "text-right" },
      {
        key: "fee",
        header: "Platform fee",
        render: (r) => (r.platformFee != null ? rm(r.platformFee) : "—"),
        className: "text-right",
        exportValue: (r) => r.platformFee ?? "",
      },
      { key: "net", header: "Seller net", render: (r) => (r.sellerNet != null ? rm(r.sellerNet) : "—"), className: "text-right", hideSm: true },
      { key: "paid", header: "Paid", render: (r) => (r.paidAt ? fmtDate(r.paidAt) : "—"), hideSm: true },
    ],
    [collectLabel],
  );

  const settlementColumns = useMemo<Column<SettlementRow>[]>(
    () => [
      { key: "shop", header: "Shop", render: (r) => r.shopName },
      {
        key: "period",
        header: "Period",
        render: (r) => `${fmtDate(r.periodStart)} – ${fmtDate(r.periodEnd)}`,
      },
      { key: "orders", header: "Orders", render: (r) => String(r.orderCount), className: "text-right" },
      { key: "net", header: "Net payout", render: (r) => rm(r.netTotal), className: "text-right" },
      {
        key: "status",
        header: "Status",
        render: (r) => (
          <Badge color={statusColor(r.status === "PAID_OUT" ? "DELIVERED" : "PENDING")}>
            {SELLER_SETTLEMENT_BATCH_STATUS_LABELS[r.status] ?? r.status}
          </Badge>
        ),
      },
    ],
    [],
  );

  const selectedOrder = orderRows.find((r) => r.id === orderSelectedId) ?? null;
  const awaitingPayoutSelected = [...selectedBatchKeys].every(
    (id) => settlementRows.find((r) => r.id === id)?.status === "FINALIZED",
  );

  const hubViews =
    hub === "orders"
      ? { options: orderViews(collectLabel), value: orderView, onChange: (v: string) => setSearchParams({ ...Object.fromEntries(searchParams), hub, view: v }) }
      : {
          options: SETTLEMENT_VIEWS,
          value: settlementView,
          onChange: (v: string) => setSearchParams({ ...Object.fromEntries(searchParams), hub, settlementStatus: v }),
        };

  return (
    <ListPageShell>
      <div className="flex gap-2">
        <Button
          variant={hub === "orders" ? "primary" : "outline"}
          size="sm"
          onClick={() => setSearchParams({ hub: "orders", view: orderView })}
        >
          <Package className="mr-1.5 inline h-4 w-4" />
          Orders
        </Button>
        <Button
          variant={hub === "settlements" ? "primary" : "outline"}
          size="sm"
          onClick={() => setSearchParams({ hub: "settlements", settlementStatus: settlementView })}
        >
          <Wallet className="mr-1.5 inline h-4 w-4" />
          Settlements
        </Button>
      </div>

      {hub === "orders" ? (
        <DataTable
          columns={orderColumns}
          rows={orderRows}
          rowKey={(r) => r.id}
          loading={showQueryLoader(ordersQuery)}
          entityLabel="Order"
          exportFilename="marketplace-orders"
          onRowClick={(r) => setOrderSelectedId(r.id)}
          activeKey={orderSelectedId ?? undefined}
          infiniteScroll={{
            hasNextPage: orderHasNext,
            isFetchingNextPage: ordersQuery.isFetchingNextPage,
            onLoadMore: () => void ordersQuery.fetchNextPage(),
          }}
          toolbar={{
            search: {
              value: searchInput,
              onChange: setSearchInput,
              onSubmit: () => {
                const next = new URLSearchParams(searchParams);
                if (searchInput.trim()) next.set("search", searchInput.trim());
                else next.delete("search");
                setSearchParams(next);
              },
              placeholder: "Order, shop, customer, tracking",
            },
            views: hubViews,
            onRefresh: () => void ordersQuery.refetch(),
          }}
          empty={{ icon: Package, title: "No marketplace orders", hint: "Paid C2C orders appear here." }}
        />
      ) : (
        <>
        {canManage && (
          <div className="mb-4 rounded-2xl border border-line bg-surface p-4">
            <p className="text-xs font-semibold text-foreground">Generate settlement batch</p>
            <p className="mt-0.5 text-[11px] text-muted">
              Include all buyer-confirmed orders not yet in a batch for the selected shop.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="shrink-0 text-xs font-medium text-muted">Seller shop</span>
              <SearchSelect
                className="min-w-[min(100%,220px)] flex-1 sm:max-w-md [&>span.mt-1]:hidden"
                placeholder="Select shop…"
                value={settleSellerId}
                onChange={setSettleSellerId}
                options={(sellersQuery.data?.items ?? []).map((s) => ({
                  value: s.id,
                  label: s.shopName,
                  hint: s.shopSlug,
                }))}
                emptyMessage="No sellers found"
              />
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  variant="outline"
                  size="md"
                  disabled={!settleSellerId}
                  onClick={() => {
                    void api<{ readyOrderCount: number; readyNetTotal: number }>(
                      `/marketplace/admin/settlements/preview?sellerId=${settleSellerId}`,
                    ).then(setSettlePreview);
                  }}
                >
                  Preview
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  disabled={!settleSellerId || generateSettleMut.isPending}
                  onClick={() => generateSettleMut.mutate()}
                >
                  Generate
                </Button>
              </div>
            </div>
            {settlePreview && (
              <p className="mt-2 text-xs text-muted">
                {settlePreview.readyOrderCount} orders · net {rm(settlePreview.readyNetTotal)}
              </p>
            )}
          </div>
        )}
        <DataTable
          columns={settlementColumns}
          rows={settlementRows}
          rowKey={(r) => r.id}
          loading={showQueryLoader(settlementsQuery)}
          entityLabel="Settlement"
          exportFilename="marketplace-settlements"
          selectable={canManage}
          selectedKeys={selectedBatchKeys}
          onSelectedKeysChange={setSelectedBatchKeys}
          onRowClick={(r) => setBatchSelectedId(r.id)}
          activeKey={batchSelectedId ?? undefined}
          infiniteScroll={{
            hasNextPage: settlementHasNext,
            isFetchingNextPage: settlementsQuery.isFetchingNextPage,
            onLoadMore: () => void settlementsQuery.fetchNextPage(),
          }}
          toolbar={{
            views: hubViews,
            onRefresh: () => void settlementsQuery.refetch(),
          }}
          selection={
            canManage && awaitingPayoutSelected && selectedBatchKeys.size > 0
              ? {
                  trailing: (
                    <Button variant="primary" size="sm" disabled={markPaidMut.isPending} onClick={() => markPaidMut.mutate([...selectedBatchKeys])}>
                      Mark paid out
                    </Button>
                  ),
                }
              : undefined
          }
          empty={{
            icon: Wallet,
            title: "No settlement batches",
            hint: "Generate a batch above or from the seller portal after buyers confirm receipt.",
          }}
        />
        </>
      )}

      <RightSidebarPanel open={!!selectedOrder} onClose={() => setOrderSelectedId(null)} title={selectedOrder?.orderNo ?? "Order"}>
        {selectedOrder && (
          <div className="space-y-3 text-sm">
            <p className="text-muted">{selectedOrder.shopName}</p>
            <p>{selectedOrder.customer}</p>
            <Badge color={fulfilmentBadgeColor(selectedOrder)}>{fulfilmentLabel(selectedOrder, collectLabel)}</Badge>
            <p className="text-xs text-muted">
              {marketplaceFulfilmentMethodLabel(selectedOrder.fulfilmentMethod, collectLabel)}
            </p>
            {selectedOrder.trackingNo && (
              <p className="text-muted">
                Tracking {selectedOrder.courier ? `${selectedOrder.courier} · ` : ""}
                {selectedOrder.trackingNo}
              </p>
            )}
            {selectedOrder.cancelReason && (
              <p className="rounded-xl bg-red-500/10 p-3 text-xs text-red-300">
                Cancelled by seller: {selectedOrder.cancelReason}. Refund the buyer from Orders.
              </p>
            )}
            <ul className="divide-y divide-line rounded-xl border border-line">
              {selectedOrder.items.map((i) => (
                <li key={i.name} className="flex justify-between px-3 py-2">
                  <span>{i.name}</span>
                  <span className="text-muted">×{i.quantity}</span>
                </li>
              ))}
            </ul>
            <div className="rounded-xl border border-line bg-surface-2/40 p-3 text-xs">
              <div className="flex justify-between gap-2">
                <span className="text-muted">Order total</span>
                <span className="font-medium tabular-nums">{rm(selectedOrder.total)}</span>
              </div>
              <div className="mt-2 flex justify-between gap-2">
                <span className="text-muted">Platform fee</span>
                <span className="font-semibold tabular-nums text-emerald-300">
                  {selectedOrder.platformFee != null ? rm(selectedOrder.platformFee) : "—"}
                </span>
              </div>
              <div className="mt-1 flex justify-between gap-2">
                <span className="text-muted">Seller net</span>
                <span className="tabular-nums">{selectedOrder.sellerNet != null ? rm(selectedOrder.sellerNet) : "—"}</span>
              </div>
            </div>
            <Link to={`/orders?record=${selectedOrder.id}`} className="text-brand-blue underline">
              Open in Orders hub
            </Link>
          </div>
        )}
      </RightSidebarPanel>

      <RightSidebarPanel
        open={!!batchSelectedId}
        onClose={() => setBatchSelectedId(null)}
        title={batchDetailQuery.data?.batch.shopName ?? "Settlement"}
        footer={
          batchDetailQuery.data &&
          (batchDetailQuery.data.batch.status === "FINALIZED" ||
            batchDetailQuery.data.batch.status === "PAID_OUT") ? (
            <div className="flex w-full flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  void apiDownload(
                    `/marketplace/admin/settlements/${batchSelectedId}/csv`,
                    `settlement-${batchSelectedId}.csv`,
                  )
                }
              >
                CSV
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  void apiDownload(
                    `/marketplace/admin/settlements/${batchSelectedId}/pdf`,
                    `settlement-${batchSelectedId}.pdf`,
                  )
                }
              >
                PDF
              </Button>
              {canManage && batchDetailQuery.data.batch.status === "FINALIZED" ? (
                <Button variant="primary" onClick={() => markPaidMut.mutate([batchSelectedId!])}>
                  Mark paid out
                </Button>
              ) : null}
            </div>
          ) : null
        }
      >
        {batchDetailQuery.data && (
          <div className="space-y-4 text-sm">
            <p className="text-muted">
              {fmtDate(batchDetailQuery.data.batch.periodStart)} – {fmtDate(batchDetailQuery.data.batch.periodEnd)}
            </p>
            <p>
              Net {rm(batchDetailQuery.data.batch.netTotal)} · {batchDetailQuery.data.batch.orderCount} orders
            </p>
            <div className="overflow-hidden rounded-xl border border-line">
              <table className="w-full text-left text-xs">
                <thead className="bg-surface-2 uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-3 py-2">Order</th>
                    <th className="px-3 py-2 text-right">Net</th>
                    <th className="px-3 py-2">Fulfilment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {batchDetailQuery.data.orders.map((o) => (
                    <tr key={o.id}>
                      <td className="px-3 py-2">
                        <Link to={`/orders?record=${o.id}`} className="text-brand-blue hover:underline">
                          {o.orderNo}
                        </Link>
                        <p className="text-muted">{o.customer}</p>
                      </td>
                      <td className="px-3 py-2 text-right">{o.sellerNet != null ? rm(o.sellerNet) : "—"}</td>
                      <td className="px-3 py-2">
                        {o.sellerFulfilmentStatus
                          ? marketplaceFulfilmentStatusLabel(o.sellerFulfilmentStatus, collectLabel)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
